/**
 * Theme Utilities
 * 
 * Centralized theme management utilities for consistent theme application
 * across the application. Handles CSS variable updates, RGB conversions,
 * and theme validation.
 */

import { ensureThemeFontLoaded } from './themeFonts';

/**
 * Convert hex color to RGB string format (for CSS rgba usage)
 * @param {string} hex - Hex color string (e.g., "#8EA58C" or "8EA58C")
 * @returns {string} - RGB string (e.g., "142, 165, 140")
 */
export const hexToRgb = (hex) => {
  // Handle undefined/null values
  if (!hex || typeof hex !== 'string') {
    console.warn('hexToRgb received invalid hex value:', hex);
    return '0, 0, 0'; // Return black as fallback
  }

  // Remove # if present
  hex = hex.replace('#', '');

  // Ensure we have a valid 6-character hex string
  if (hex.length !== 6) {
    console.warn('hexToRgb received invalid hex length:', hex);
    return '0, 0, 0'; // Return black as fallback
  }

  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
};

/**
 * Validate theme colors object
 * @param {Object} colors - Theme colors object
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateThemeColors = (colors) => {
  if (!colors || typeof colors !== 'object' || Array.isArray(colors)) {
    console.warn('validateThemeColors: colors is not a valid object', colors);
    return false;
  }

  const requiredColors = ['primary', 'secondary', 'accent', 'neutral', 'surface'];
  // Regex to match hex colors: # followed by 3 or 6 hex digits
  const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

  for (const colorKey of requiredColors) {
    // Check if property exists and is accessible
    if (!(colorKey in colors)) {
      console.warn(`validateThemeColors: Missing color key '${colorKey}' in colors object`);
      return false;
    }

    const colorValue = colors[colorKey];
    
    // Check if value exists and is a string
    if (!colorValue || typeof colorValue !== 'string') {
      console.warn(`validateThemeColors: Invalid color value for '${colorKey}':`, colorValue, typeof colorValue);
      return false;
    }
    
    // Normalize: ensure it starts with # and trim whitespace
    let normalizedValue = String(colorValue).trim();
    if (!normalizedValue.startsWith('#')) {
      normalizedValue = `#${normalizedValue}`;
    }
    
    // Test the normalized value (should have # prefix now)
    if (!hexColorRegex.test(normalizedValue)) {
      console.warn(`validateThemeColors: Invalid hex format for '${colorKey}':`, {
        original: colorValue,
        normalized: normalizedValue,
        type: typeof colorValue,
        length: normalizedValue.length
      });
      return false;
    }
  }

  return true;
};

/**
 * Normalize hex color (ensure it has # prefix)
 * @param {string} hex - Hex color string
 * @returns {string} - Normalized hex color with # prefix
 */
export const normalizeHexColor = (hex) => {
  if (!hex || typeof hex !== 'string') {
    return '#000000';
  }
  return hex.startsWith('#') ? hex : `#${hex}`;
};

/**
 * Apply theme colors and font to CSS variables
 * This is the single source of truth for theme application
 * 
 * @param {Object} theme - Theme object with colors and optional font
 * @param {Object} theme.colors - Color object with primary, secondary, accent, neutral, surface
 * @param {string} [theme.font] - Font family string (optional)
 * @param {HTMLElement} [rootElement] - Root element to apply theme to (defaults to document.documentElement)
 */
export const applyTheme = (theme, rootElement = null) => {
  const root = rootElement || document.documentElement;
  
  if (!theme) {
    console.error('applyTheme: No theme object provided');
    return;
  }

  // Handle different input formats
  let colors = theme.colors || theme;
  const font = theme.font || null;

  // If theme.colors doesn't exist, assume theme itself is the colors object
  if (!colors || typeof colors !== 'object') {
    console.error('applyTheme: Invalid theme object provided', theme);
    return;
  }

  // Validate colors
  if (!validateThemeColors(colors)) {
    console.error('applyTheme: Invalid theme colors provided', {
      theme,
      colors,
      colorsType: typeof colors,
      isArray: Array.isArray(colors),
      keys: colors ? Object.keys(colors) : null
    });
    return;
  }

  // Normalize hex colors (ensure # prefix)
  const normalizedColors = {
    primary: normalizeHexColor(colors.primary),
    secondary: normalizeHexColor(colors.secondary),
    accent: normalizeHexColor(colors.accent),
    neutral: normalizeHexColor(colors.neutral),
    surface: normalizeHexColor(colors.surface),
  };

  // Set hex colors as CSS variables
  root.style.setProperty('--color-primary', normalizedColors.primary);
  root.style.setProperty('--color-secondary', normalizedColors.secondary);
  root.style.setProperty('--color-accent', normalizedColors.accent);
  root.style.setProperty('--color-neutral', normalizedColors.neutral);
  root.style.setProperty('--color-surface', normalizedColors.surface);

  // Set RGB values for opacity calculations
  root.style.setProperty('--color-primary-rgb', hexToRgb(normalizedColors.primary));
  root.style.setProperty('--color-secondary-rgb', hexToRgb(normalizedColors.secondary));
  root.style.setProperty('--color-accent-rgb', hexToRgb(normalizedColors.accent));
  root.style.setProperty('--color-neutral-rgb', hexToRgb(normalizedColors.neutral));
  root.style.setProperty('--color-surface-rgb', hexToRgb(normalizedColors.surface));

  // Set font family if provided
  if (font) {
    root.style.setProperty('--font-family', font);
    // The stylesheet for the chosen webfont is not bundled: request it here so
    // the browser can render the family instead of silently falling back.
    ensureThemeFontLoaded(font);
  }

  // Dispatch custom event for theme change
  // This allows components to react to theme changes
  const themeChangeEvent = new CustomEvent('themechange', {
    detail: { colors: normalizedColors, font }
  });
  window.dispatchEvent(themeChangeEvent);
};

/**
 * Get current theme from CSS variables
 * Useful for reading the currently applied theme
 * 
 * @param {HTMLElement} [rootElement] - Root element to read from (defaults to document.documentElement)
 * @returns {Object} - Current theme object with colors and font
 */
export const getCurrentTheme = (rootElement = null) => {
  const root = rootElement || document.documentElement;
  const computedStyle = getComputedStyle(root);

  return {
    colors: {
      primary: computedStyle.getPropertyValue('--color-primary').trim() || '#556D53',
      secondary: computedStyle.getPropertyValue('--color-secondary').trim() || '#5C4033',
      accent: computedStyle.getPropertyValue('--color-accent').trim() || '#BFCFBB',
      neutral: computedStyle.getPropertyValue('--color-neutral').trim() || '#D9C2A8',
      surface: computedStyle.getPropertyValue('--color-surface').trim() || '#ffffff',
    },
    font: computedStyle.getPropertyValue('--font-family').trim() || null,
  };
};

